// @file Composable that provides methods for sorting sections
import sectionSorter from '@@/bits/section_sorter'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import type { Section } from '@@/types'
import { storeToRefs } from 'pinia'

export const useSectionSorterHelper = (): {
  getNewIndexBetween: typeof getNewIndexBetween
  getSortIndexAndGapSize: typeof getSortIndexAndGapSize
} => {
  const getSortIndex = (timestamp?: Date): number => {
    if (timestamp == null) {
      timestamp = new Date()
    }
    return new Date(timestamp.toISOString()).getTime() / 1000
  }
  const getTopSortIndex = (): number => {
    return -getSortIndex()
  }
  const getBottomSortIndex = (): number => {
    return getSortIndex()
  }

  const getNewIndexBetween = (
    previousSection?: Section | null,
    nextSection?: Section | null,
  ): { sortIndex?: number; gapSize?: number } => {
    // Do nothing if either group is null
    if (previousSection == null || nextSection == null) {
      return {}
    }
    let prevIndex: number | null = previousSection?.sort_index ?? null
    let nextIndex: number | null = nextSection?.sort_index ?? null

    if (prevIndex == null) {
      // moved to top
      prevIndex = getTopSortIndex()
    }

    if (nextIndex == null) {
      // moved to bottom
      nextIndex = getBottomSortIndex()
    }

    // New sort index is the average of the two adjacent sections' sort indices
    const sortIndex = Math.round((prevIndex + nextIndex) / 2)
    // Might be NaN, which translates to a nil in controller.
    const gapSize = Math.abs(nextIndex - prevIndex)
    return { sortIndex, gapSize }
  }

  const getSortIndexAndGapSize = (
    targetSectionId: number,
    relativePositionToTarget: 'before' | 'after',
  ): { sortIndex?: number; gapSize?: number } => {
    const { sortedSections } = storeToRefs(useSurfaceSectionsStore())
    const targetIndex = sortedSections.value.findIndex((section) => section.id === targetSectionId)
    let previousSection: Section | null = null
    let nextSection: Section | null = null
    if (relativePositionToTarget === 'before') {
      previousSection = sortedSections.value[targetIndex - 1]
      nextSection = sortedSections.value[targetIndex]
    } else {
      previousSection = sortedSections.value[targetIndex]
      nextSection = sortedSections.value[targetIndex + 1]
    }
    const { sortIndex, gapSize } = (() => {
      // dropping at the start of the list
      if (previousSection == null) {
        const newSortIndex = sectionSorter.getTopSortIndex()
        return {
          sortIndex: newSortIndex,
          gapSize: Math.abs(newSortIndex - sortedSections.value[0].sort_index),
        }
      }
      // dropping at the end of the list
      if (nextSection == null) {
        const newSortIndex = sectionSorter.getBottomSortIndex()
        return {
          sortIndex: newSortIndex,
          gapSize: Math.abs(newSortIndex - sortedSections.value[sortedSections.value.length - 1].sort_index),
        }
      }
      // dropping between two sections
      return getNewIndexBetween(previousSection, nextSection)
    })()
    if (sortIndex === undefined || gapSize === undefined) return { sortIndex: undefined, gapSize: undefined }
    return { sortIndex, gapSize }
  }

  return { getNewIndexBetween, getSortIndexAndGapSize }
}
